import styled from '@emotion/styled';

import { useAppSelector } from '../../../../shared/store';

import { buildSupportedDeliveryInfo } from '../../shared/utils/deliveryTextBuilder';

import type { AcceptedProduct } from '../../types';

import Name from '../components/ATF/Name';
import Price from '../components/ATF/Price';
import ExtraBenefit from '../components/ATF/ExtraBenefit';
import ProductCouponBanner from '../../shared/coupon/ProductCouponBanner';
import ProductInfoList from '../components/ATF/ProductInfoList';
import MemberCouponGuide from '../components/ATF/MemberCouponGuide';
import ProductOption from '../components/ATF/option-section/ProductOption';
import AdultInformation from '../components/description/AdultInformation';
import ProductBuyPrice from '../components/ProductBuy/ProductBuyPrice';
import ProductBuyButtonGroup from '../components/ProductBuy/ProductBuyButtonGroup';

import COLOR from '../../../../shared/constant/colorset';
import { NoMainImageLogo } from '../../../../shared/images';

const Container = styled.main`
  display: flex;
  justify-content: space-between;
`;

const ProductMainImage = styled.div<{ imageUrl: string }>`
  width: 430px;
  height: 552px;
  background: url(${(props) => props.imageUrl}), url(${NoMainImageLogo}) no-repeat 50% 50%;
  background-size: cover, contain;
  background-color: ${COLOR.kurlyGray150};
`;

const ATFInfoWrapper = styled.section`
  width: 560px;
`;

const ProductBuyWrapper = styled.div`
  padding-bottom: 40px;
`;

const ProductBuyPriceWrapper = styled.div`
  padding-top: 30px;
`;

interface Props {
  product: AcceptedProduct;
}

export default function ATFContainer({ product }: Props) {
  const isGuest = useAppSelector(({ auth }) => auth.isGuest);
  const couponBanner = useAppSelector(({ productDetail }) => productDetail.couponBanner);

  const {
    isOnlyAdult,
    mainImageUrl,
    name,
    shortDescription,
    sellerName,
    legacyPromotion,
    discountRate,
    discountedPrice,
    basePrice,
    retailPrice,
    isExpectedPoint,
    expectedPointRatio,
    expectedPoint,
    isMultiplePrice,
    notSalesText,
    afterSaleServiceInfo,
    salesUnit,
    volume,
    deliveryTypeNames,
    productOrigin,
    todayBrix,
    soldOutText,
    storageTypes,
    allergy,
    expirationDate,
    extraInfos,
    guide,
    memberCoupon: { newbieLimitDatetime, newbieMinPrice },
  } = product;

  const { deliveryName, deliveryGuide, isDelivery } = buildSupportedDeliveryInfo(deliveryTypeNames);

  return (
    <Container id="product-atf">
      <ProductMainImage imageUrl={mainImageUrl} />
      <ATFInfoWrapper>
        <Name deliveryTypeName={deliveryName} name={name} shortDescription={shortDescription} />
        <Price
          discountRate={discountRate}
          discountedPrice={discountedPrice}
          basePrice={basePrice}
          retailPrice={retailPrice}
          isMultiplePrice={isMultiplePrice}
        />
        {legacyPromotion === 'NEWBIE' && newbieLimitDatetime && (
          <MemberCouponGuide newbieLimitDatetime={newbieLimitDatetime} newbieMinPrice={newbieMinPrice} />
        )}
        <ExtraBenefit
          isExpectedPoint={isExpectedPoint}
          expectedPointRatio={expectedPointRatio}
          expectedPoint={expectedPoint}
          isDiscountProduct={!!discountedPrice}
        />
        {couponBanner && (
          <ProductCouponBanner
            type={couponBanner.type}
            bannerName={couponBanner.bannerName}
            bannerLink={couponBanner.bannerLink}
            couponAccessKey={couponBanner.accessKey}
            extraInfo={couponBanner.extraInfo}
          />
        )}
        <ProductInfoList
          deliveryName={deliveryName}
          deliveryGuide={deliveryGuide}
          isDelivery={isDelivery}
          salesUnit={salesUnit}
          volume={volume}
          todayBrix={todayBrix}
          sellerName={sellerName}
          productOrigin={productOrigin}
          storageTypes={storageTypes}
          allergy={allergy}
          expirationDate={expirationDate}
          soldOutText={soldOutText}
          extraInfos={extraInfos}
          guide={guide}
          notSalesText={notSalesText}
          afterSaleServiceInfo={afterSaleServiceInfo}
        />
        <ProductBuyWrapper>
          <ProductOption />
          {isOnlyAdult && <AdultInformation />}
          <ProductBuyPriceWrapper>
            <ProductBuyPrice
              loggedIn={!isGuest}
              isExpectedPoint={isExpectedPoint}
              isDiscountProduct={!!discountedPrice}
            />
          </ProductBuyPriceWrapper>
          <ProductBuyButtonGroup />
        </ProductBuyWrapper>
      </ATFInfoWrapper>
    </Container>
  );
}
